import {
    WorkflowDto,
    WorkflowStatusDto,
    WorkflowDtoType, RoleDto, RejectedApprovalDto,
} from "./Client";

import {EditableValue} from '../components/BasicDataGrid';
import {IStateProperty} from "../components/BaseColumn";
import {ColorFactory} from "./ColumnFactory";

/** Convert from workflow status modifier string to data grid editable value. */
const modifier2editable = (str: string): EditableValue => {
        return EditableValue.Editable;
};

/** Convert from data grid editable valueto workflow status modifier string . */
const editable2modifier = (modifier: EditableValue): string => {
        return "";
};

interface IParsedWorkflowInfo {
    editable: EditableValue;
    workflow: WorkflowDto;
    status: WorkflowStatusDto;
}

/** Parse status string as received from the back end and split it into components. */
export const parseWorkflowString = (status: string | undefined, workflowList: WorkflowDto[]): IParsedWorkflowInfo | undefined => {
    if (status && status.length > 0) {
        const editable = modifier2editable(status[0]);

        const components = status.split("_");
        if (components.length > 3 || components.length < 2) {
            return undefined;
        }
        const wfObjId = parseInt(components[0], 10);
        const stObjId = parseInt(components[1], 10);

        // find the workflow with the given object id
        const workflow = workflowList.find((wf: WorkflowDto) => wf.objectId === wfObjId);
        if (workflow && workflow.status) {
            // find the workflow status with the given object id
            const workflowStatus = workflow.status.find( (wfs: WorkflowStatusDto) => wfs.objectId === stObjId);
            if (workflowStatus) {
                return {editable: editable, workflow: workflow, status: workflowStatus};
            }
        }
    }
    return undefined;
};

/**
 * Create the data grid valueStates for a given status string.
 *
 * @param status the status string as received from the back end
 * @param workflowList all workflows that are searched for the status
 * @param info if already parsed workflow string, can be used for optimization
 * @param rejectedStates rejected states
 */
const status2valueStates = (rejectedStates: RejectedApprovalDto[], info?: IParsedWorkflowInfo): IStateProperty[] => {
    return (info && info.workflow.status)
        ? info.workflow.status.map((wfs: WorkflowStatusDto) => {
            const rejectedState = rejectedStates.find( state => state.workflowStatus !== undefined && state.workflowStatus.objectId === wfs.objectId);
            return {
                id: wfs.objectId,
                role: wfs.role.name,
                roleId: wfs.role.objectId,
                color: ColorFactory.color(wfs.sequenceNumber),
                hoverText: wfs.name,
                rejectReason: rejectedState ?
                    rejectedState.reason ? rejectedState.reason : ""
                    : "",
                type: wfs.type,
                isResetPoint: wfs.resetPoint
            };
        })
        : [];
};

/**
 * Create a human readable form of the status valueState number used by the data grid.
 *
 * @param valueState the status valueState number used by the data grid
 * @param workflowList all available workflows that are searched for the valueState
 * @returns the name of the status in the workflow
 */
export const valueState2str = (valueState: number | undefined, workflowList: WorkflowDto[]): string | undefined => {
    let str: string | undefined = undefined;
    for (let wfIdx = 0; wfIdx < workflowList.length; wfIdx++) {
        if (str) break;

        const wf: WorkflowDto = workflowList[wfIdx];
        if (!wf.status) continue;

        for (let wfsIdx = 0; wfsIdx < wf.status.length; wfsIdx++) {
            if (str) break;

            const wfs = wf.status[wfsIdx];
            if (wfs.objectId === valueState) {
                str = wfs.type;
            }
        }
    }
    return str;
};

/**
 * Create a back end compatible status string (e.g. "?16_28")
 * from a value state.
 *
 * @param statusValueState the new value state
 * @param status the status string as received from the back end
 * @param workflowList all workflows that are searched for the status
 * @return the status string or undefined if an error occured
 */
export const getDtoStatus = (statusValueState: number, status: string, workflowList: WorkflowDto[]): string => {
    // We keep the original modifier and workflow but may have a new status id.
    const info = parseWorkflowString(status, workflowList);
    return info
        ? combineStatus(info.editable, info.workflow, statusValueState)
        : "";
};

/**
 * Create a back end compatible status string (e.g. "?16_28")
 * from a given values.
 *
 * @param editable editable property of the value
 * @param workflow workflow used for the status
 * @param statusValueState workflow state
 * @return the status string
 */
function combineStatus(editable: EditableValue, workflow: WorkflowDto, statusValueState: number): string {
    return editable2modifier(editable) + workflow.objectId + '_' + statusValueState;
}

export interface IRowStatusProps {
    statusView?: string;
    statusValueState?: number;
    statusValueStateEditable?: EditableValue;
    statusValueStateResetable?: boolean;
    statusValueStates: IStateProperty[];
    resetRoles: RoleDto[];
    workFlowName?:string;
    workflowResetPointRejection?: boolean;
    isDependentOverwriteSpecific?: boolean;
    invalidTexts?: boolean;
    statusStepIndex?: number;
    workflowType?: WorkflowDtoType;
    hasChangeWfButton?: boolean;
    optionBasic?: any;
}

export interface IPSLApproval {
    rejectMessage: string;
    approved: boolean;
}

/**
 * Create all necessary status properties for a given status.
 * These properties are supposed to become part of each row
 * shown in the BasicDataGrid.
 *
 * @param status the status string as received from the back end
 * @param rejectedStates rejected states of the current workflow
 * @param workflowList all workflows that are searched for the status
 * @returns an object containing status properties for a row
 */
export const getRowStatusProps = (status: string, rejectedStates: RejectedApprovalDto[], workflowList: WorkflowDto[]): IRowStatusProps => {
    if (!status) {
        throw Error("Invalid status: " + status);
    }
    const info: IParsedWorkflowInfo | undefined = parseWorkflowString(status, workflowList);
    return {
        statusView: info ? info.status.type : status,
        statusValueState: info ? info.status.objectId : undefined,
        statusValueStateEditable: info && info.editable,
        statusValueStateResetable: status.includes("_RESET"),
        workFlowName: info ? info.workflow.name : "",
        workflowType: info && info.workflow.type,
        workflowResetPointRejection: info && info.workflow.resetPointRejection,
        statusValueStates: status2valueStates(rejectedStates, info),
        resetRoles: info && info.workflow && info.workflow.resetRoles ? info.workflow.resetRoles : [],
    };
};

export function sortWorkflowsByName(a: WorkflowDto, b: WorkflowDto): number {
    return (a.name).localeCompare(b.name, undefined, {sensitivity: 'base'});
}



// WEBPACK FOOTER //
// ./src/services/WorkflowUtils.ts